import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import { Picture } from "../../image/src";

export interface DemoAddOnProps {
    headingText?: string | null;
    bodyText?: string | null;
    linkText?: string | null;
    ctaHref: string;
}

const containerCss = css({
    "maxWidth": 968,
    "margin": "0 auto",
    "padding": token("space.600"),
    "display": "flex",
    "justifyContent": "start",
    "borderRadius": wacToken("corner.radius.card"),
    [media.below.sm]: {
        flexDirection: "column",
        alignItems: "center",
        gap: 21,
        paddingBlock: token("space.400"),
        paddingInline: token("space.300")
    },
    "boxShadow": token("elevation.shadow.raised"),
    "transition": "box-shadow 0.2s ease-in-out",
    ":hover": {
        boxShadow: token("elevation.shadow.overlay")
    },
    "gap": token("space.600")
});

const sectionRightCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200")
});

const headingTextCss = css({
    fontSize: 24,
    fontWeight: 700,
    lineHeight: "30px",
    margin: 0,
    color: wacToken("color.primary.black")
});

const bodyTextCss = css({
    margin: 0,
    color: wacToken("color.primary.black")
});

const svgStyles = css({
    width: "155.19px",
    height: "133.73px",
    [media.below.md]: {
        width: "91px",
        height: "78.3px"
    }
});

export const DemoAddOnImage = () => {
    return (
        <Picture
            image={{
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/rl8k3b0oo518e2ag657731kn07r84m04.svg",
                alt: ""
            }}
            css={svgStyles}
        />
    );
};

export const DemoAddOn = ({
    headingText,
    bodyText,
    linkText,
    ctaHref
}: DemoAddOnProps) => {
    const { formatMessage } = useIntl();
    const defaultHeadingText = formatMessage({
        id: "PricingPages.atlassianGuard.demoAddOn.heading",
        defaultMessage: "See what Atlassian Guard can do for you"
    });

    const defaultBodyText = formatMessage({
        id: "PricingPages.atlassianGuard.demoAddOn.body",
        defaultMessage:
            "Join our live monthly demo to gain practical guidance for implementing security best practices and controls across Atlassian cloud."
    });

    const defaultLinkText = formatMessage({
        id: "PricingPages.atlassianGuard.demoAddOn.cta",
        defaultMessage: "Register for the demo"
    });

    const handleClick = () => {
        if (ctaHref) {
            window.location.href = ctaHref;
        }
    };

    const clickableCardStyle = css({
        cursor: `${ctaHref ? "pointer" : "default"}`
    });

    return (
        <div
            css={[containerCss, clickableCardStyle]}
            tabIndex={0}
            onClick={handleClick}
            role="link"
            onKeyDown={(e) => {
                if (e.code == "Enter" || e.code == "Space") {
                    e.preventDefault();
                    handleClick();
                }
            }}
        >
            <DemoAddOnImage />
            <div css={sectionRightCss}>
                <h4 css={headingTextCss} id="atlassian-guard-add-on-heading">
                    {headingText ?? defaultHeadingText}
                </h4>

                {bodyText !== "" && (
                    <p css={bodyTextCss}>{bodyText ?? defaultBodyText}</p>
                )}

                <Link
                    text={linkText ?? defaultLinkText}
                    type="primary"
                    href={ctaHref}
                    id="atlassian-guard-add-on-cta"
                    ariaLabelledby="atlassian-guard-addon-cta atlassian-guard-add-on-heading"
                />
            </div>
        </div>
    );
};

DemoAddOn.displayName = "DemoAddOnSection";
